import { isDefined, uppercaseFirst } from '@myre/common-primitives';

import { format, formatISO, isValid, parseISO } from 'date-fns';
import { get, identity } from 'lodash-es';

import { isValidJSKey } from './helpers';
import { defaultI18n, languages } from './i18n.const';
import { type I18n } from './i18n.types';
import { getWordingWithDots } from './wording.helpers';

export interface Interpolations {
  key: string;
  isDate: boolean;
  isTranslate: boolean;
}

export type Context = 'translation' | 'binding';
export type ContextCondition = false | Context | 'all';

export type Sanitizer = (value: string, context: Context) => string;

export interface TranslateOptions<B extends Bindings = any> {
  i18n?: I18n;
  bindings?: B;
  sanitizer?: Sanitizer;
  escape?: ContextCondition;
  htmlToText?: ContextCondition;
  fallback?: string;
}

export interface TranslateBindingOptions<
  B extends Bindings,
> extends TranslateOptions<B> {
  bindings: B;
}

export type Translator<B extends Bindings = any> = (
  key: string,
  options?: TranslateOptions<B>,
) => string;

export class Pipe {
  private readonly _name: string;
  private readonly _args: any[];

  constructor(val: string) {
    // replace unescaped semicolon with \u000B (very unusual character) in order not to split correctly
    const args = val
      .replace(/([^\\]):/g, '$1\u000B')
      .split('\u000B')
      .map(v => v.replace(/\\/g, '').trim());

    this._name = args.shift();
    this._args = args;
  }

  public get name(): string {
    return this._name;
  }

  public get args(): string[] {
    return this._args;
  }
}

export class InvalidBindingKeyError extends Error {
  private readonly _key: string;

  constructor(key: string) {
    super(`Invalid binding key: ${key}`);

    this._key = key;
  }

  public get key(): string {
    return this._key;
  }
}

class Binding {
  private readonly _key: string;
  private readonly _pipes: Pipe[] = [];

  constructor(
    binding: string,
    options: {
      hasBindings: boolean;
    },
  ) {
    const { hasBindings } = options;

    const trimmed = binding.trim();

    const values = trimmed.split('|').map(v => v.trim());
    this._key = values.shift();

    const stringValue = extractStringValue(this._key);

    this._key = stringValue || this._key;

    for (const val of values) {
      this._pipes.push(new Pipe(val));
    }

    const parts = this._key.split('.');

    if (parts.some(part => !isValidJSKey(part))) {
      throw new InvalidBindingKeyError(this._key);
    }

    if (hasBindings && !stringValue && this._pipes[0]?.name !== 'translate') {
      this._pipes.unshift(new Pipe('bind'));
    }
  }

  public get key(): string {
    return this._key;
  }

  public get pipes(): Pipe[] {
    return this._pipes;
  }
}

export type Bindings = {
  [key: string]: string | number | Date | Bindings;
};

interface PipeOptions {
  i18n: I18n;
  bindings: Bindings | ((key: string) => string);
  sanitizer: Sanitizer;
}

export function extractInterpolationKeys(str: string): Interpolations[] {
  const keys: Interpolations[] = [];

  const interpolation = new Interpolation(str);

  interpolation.registerHandler((key, pipes) => {
    keys.push({
      key,
      isDate: pipes.some(p => p.name === 'date'),
      isTranslate: pipes.some(p => p.name === 'translate'),
    });

    return '';
  });

  interpolation.interpolate(defaultI18n);

  return keys.reverse();
}

function datePipeHandler(
  input: string,
  pipe: Pipe,
  options: PipeOptions,
): string {
  const { i18n } = options;

  const parsed = parseISO(input);

  return isValid(parsed)
    ? format(parseISO(input), pipe.args[0], {
        locale: languages[i18n.lang].dateFnsLocale,
      })
    : input;
}

function toUpperPipeHandler(input: string): string {
  return input.toUpperCase();
}

function toLowerPipeHandler(input: string): string {
  return input.toLowerCase();
}

function uppercaseFirstPipeHandler(input: string): string {
  return uppercaseFirst(input);
}

function extractStringValue(value: string): string {
  const match = /^'(.*)'$|^"(.*)"$/.exec(value);

  if (!match) {
    return null;
  }

  return match[1] || match[2];
}

function bindPipeHandler(
  input: string,
  pipe: Pipe,
  options: PipeOptions,
): string {
  const stringValue = extractStringValue(input);

  if (stringValue) {
    return stringValue;
  }

  const { bindings, sanitizer } = options;

  const value =
    typeof bindings === 'function'
      ? bindings(input)
      : (get(bindings, input) ?? pipe.args[0]);

  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    return formatISO(value);
  }

  return (sanitizer || identity)(String(value), 'binding');
}

export class Interpolation {
  private readonly _pipesHandlers: Record<
    string,
    (input: string, pipe: Pipe, options: PipeOptions) => string
  > = {
    date: datePipeHandler,
    lowercase: toLowerPipeHandler,
    uppercase: toUpperPipeHandler,
    uppercasefirst: uppercaseFirstPipeHandler,
    bind: bindPipeHandler,
    translate: identity,
  };

  constructor(
    private readonly _str: string,
    private readonly _options: {
      bindings?: Bindings | ((key: string) => string);
      sanitizer?: Sanitizer;
    } = {},
  ) {}

  public interpolate(i18n: I18n): string {
    return this._interpolate(this._str, i18n);
  }

  public registerHandler(
    handler: (input: string, pipes?: Pipe[]) => string,
  ): void {
    this._handler = handler;
  }

  public registerTranslator(
    translator: (input: string, i18n: I18n) => string,
  ): void {
    this._pipesHandlers.translate = (input, _, { i18n, sanitizer }) =>
      translator((sanitizer || identity)(input, 'translation'), i18n);
  }

  private _interpolate(str: string, i18n: I18n): string {
    const interpolationStart = str.indexOf('{{');

    if (interpolationStart === -1) {
      return str;
    }

    const firstPart = str.substring(0, interpolationStart);
    const secondPart = this._interpolate(
      str.substring(interpolationStart + 2),
      i18n,
    );

    const interpolationEnd = secondPart.indexOf('}}');

    if (interpolationEnd === -1) {
      return str;
    }

    const binding = secondPart.substring(0, interpolationEnd);
    const thirdPart = this._interpolate(
      secondPart.substring(interpolationEnd + 2),
      i18n,
    );

    return `${firstPart}${this._handleString(binding, i18n)}${thirdPart}`;
  }

  private _handleString(str: string, i18n: I18n): string {
    i18n ||= defaultI18n;

    const binding = new Binding(str, {
      hasBindings: !!this._options.bindings,
    });

    const result = this._handler(binding.key, binding.pipes);
    const hasResult = isDefined(result);

    if (!hasResult) {
      return '';
    }

    return binding.pipes.reduce((acc, pipe) => {
      if (!this._options.bindings && pipe.name === 'bind') {
        return acc;
      }

      const pipeHandler = this._pipesHandlers[pipe.name];

      return pipeHandler(acc, pipe, {
        i18n,
        bindings: this._options.bindings,
        sanitizer: this._options.sanitizer,
      });
    }, result.toString());
  }

  private _handler: (input: string, pipes?: Pipe[]) => string = str =>
    str.toString();
}

export function interpolate<B extends Bindings>(
  rawStr: string,
  options?: TranslateOptions<B>,
  wordings?: any,
): string {
  return interpolateAndTranslate(rawStr, options, (key, opts) => {
    const wording = getWordingWithDots(key, wordings);

    if (!wording) {
      return key;
    }

    return typeof wording === 'function' ? wording(opts) : wording;
  });
}

export function interpolateAndTranslate<B extends Bindings>(
  rawStr: string,
  options: TranslateOptions<B>,
  translator: Translator<B> | null,
): string {
  options = {
    ...options,
  };

  const sanitizer = options.sanitizer || (value => value);

  const interpolation = new Interpolation(rawStr, {
    sanitizer,
    bindings: options.bindings,
  });

  interpolation.registerTranslator(input => translator(input, options));

  return interpolation.interpolate(options.i18n);
}
